// src/components/WorkoutDetails/ExerciseCard.js
import React, { useState, useContext, useEffect, useRef } from "react";
import TimerControl from "./TimerControl";
import describeTempo from "../Workout/TempoDesc";
import FormatInstructions from "../FormatInstructions";

import LanguageContext from "../../../context/LanguageContext";

// Import data
const jsonDataEn = require("../../data/data-en.json");
const jsonDataFr = require("../../data/data-fr.json");

const ExerciseCard = ({ exercise, index, state, setExerciseStates, toggleInstructions, isOpen, showGif, toggleGif }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const { language } = useContext(LanguageContext);
  const data = language === "en" ? jsonDataEn.data : jsonDataFr.data;

  useEffect(() => {
    if (!showTooltip) return;
    const onDown = (e) => {
      if (iconRef.current && !iconRef.current.contains(e.target) && tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showTooltip]);

  // locale-aware exercise fields
  const displayName = language === "en" ? exercise.name : exercise.name_fr;
  const primaryMuscle = language === "en" ? exercise.primary_muscle : exercise.primary_muscle_fr;
  const secondaryMuscle = language === "en" ? exercise.secondary_muscle : exercise.secondary_muscle_fr;
  const tertiaryMuscle = language === "en" ? exercise.tertiary_muscle : exercise.tertiary_muscle_fr;
  const equipmentName = language === "en" ? exercise.equipment_names : exercise.equipment_names_fr;
  const instructions = language === "en" ? exercise.instructions : exercise.instructions_fr;

  return (
    <div className={state.completed ? "exercise-border-done exercise-container-done mb-5" : "exercise-container mb-5"}>
      <div className={state.completed ? "number-badge-done" : "number-badge"}>{index + 1}</div>

      <div className="row">
        <div className="">
          <div className="row align-items-center">
            <h2 className={`text-center ${state.completed ? "text-color-done" : ""}`}>{displayName}</h2>
          </div>

          <div className="row">
            <div className="col">
              <hr className="red-divider" />
            </div>
          </div>

          {/* GIF Toggle */}
          <div className="fw-bold text-start" onClick={() => toggleGif(index)} style={{ cursor: "pointer" }}>
            <p className="gif-text">
              {showGif ? "Hide GIF" : "Show GIF"} <i className={`arrow ${showGif ? "up" : "down"}`}></i>
            </p>
          </div>

          {showGif && (
            <div className="mt-3 mb-3">
              <img src={`assets/exercise-gif/${exercise.name}.gif`} className="img-fluid rounded" alt={`${displayName} Exercise Animation`} />
            </div>
          )}

          {/* Muscle Groups */}
          <div className="row">
            <div className="col mt-3 mb-3">
              {primaryMuscle && <span className="muscles-badge badge-danger me-1">{primaryMuscle}</span>}
              {secondaryMuscle && <span className="muscles-badge badge-warning me-1">{secondaryMuscle}</span>}
              {tertiaryMuscle && <span className="muscles-badge badge-success">{tertiaryMuscle}</span>}
            </div>
          </div>

          {/* Equipment */}
          <div className="row">
            {equipmentName && (
              <p className="text-start text-muted">
                <strong>{data.workout.equipment}:</strong> {equipmentName === "N/A" ? "Bodyweight" : equipmentName}
              </p>
            )}
          </div>

          {/* Exercise Attributes */}
          <div className="d-flex flex-wrap align-items-center">
            <div className="col-8">
              {exercise.tempo && (
                <div className="row">
                  <div className="col text-start">
                    <div className="tempo-container">
                      <strong>Tempo:</strong> {exercise.tempo}
                      <span ref={iconRef} className="info-icon" onClick={() => setShowTooltip((v) => !v)} aria-label="More info about tempo" role="button" aria-expanded={showTooltip}>
                        <strong>?</strong>
                      </span>
                      <div ref={tooltipRef} className={`tooltip-box${showTooltip ? " visible" : ""}`} role="tooltip">
                        <div>
                          {data.workout.tempoBefore}
                          <code>{exercise.tempo}</code>
                          {data.workout.tempoAfter}
                          <br />
                          {describeTempo(data.workout.tempo, exercise.tempo)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sets */}
              {exercise.sets && (
                <div className="row">
                  <div className="col text-start">
                    <strong>{data.workout.sets}:</strong> {exercise.sets}{" "}
                    <span className={`${state.completed ? "gif-text-done" : "gif-text"}`}>
                      (<span className="fw-bold">{exercise.sets - state.sets}</span> {data.workout.remaining})
                    </span>
                  </div>
                </div>
              )}

              {/* Reps */}
              {exercise.reps != null && (
                <div className="row">
                  <div className="col text-start">
                    {(() => {
                      const repsStr = exercise.reps.toString();
                      const isDuration = repsStr.endsWith("min");
                      return (
                        <>
                          <strong>{isDuration ? `${data.workout.duration}:` : `${data.workout.reps}:`}</strong> {repsStr}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Rest */}
              {exercise.rest && exercise.rest !== "N/A" && (
                <div className="row">
                  <div className="col text-start">
                    <strong>{data.workout.rest}:</strong> {exercise.rest}
                  </div>
                </div>
              )}
            </div>

            <div className="col-4 d-flex flex-column align-items-end">
              <TimerControl exercise={exercise} state={state} setExerciseStates={setExerciseStates} />
            </div>
          </div>

          {/* Instructions Toggle */}
          <div className="text-muted fw-bold d-flex align-items-center">
            <div className="text-start" onClick={() => toggleInstructions(index)} style={{ cursor: "pointer" }}>
              <p className="me-2 mb-0">
                {data.workout.instructions} <i className={`arrow ${isOpen ? "up" : "down"}`}></i>
              </p>
            </div>
          </div>

          {isOpen && (
            <div className="row">
              <div className="col">
                <FormatInstructions raw={instructions} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseCard;
